import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { listWorkspaceInspections } from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

export default function Inspections() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const rfqId = params.get("rfq_id") || undefined;
  const buyerId = params.get("buyer_id") || undefined;
  const poId = params.get("po_id") || undefined;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await listWorkspaceInspections({
          rfq_id: rfqId,
          buyer_id: buyerId,
          po_id: poId,
        });
        setRows(res.inspections || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load inspections");
      } finally {
        setLoading(false);
      }
    })();
  }, [rfqId, buyerId, poId]);

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.inspectionList}>
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              Inspections
            </span>
          </Link>
          <Link
            to="/workspace/purchase-orders"
            className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 hover:text-neutral-800"
          >
            Purchase Orders
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
          Workspace
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-light tracking-tight">Inspections</h1>
        <p className="mt-3 text-sm text-neutral-500 max-w-2xl">
          Pre-shipment inspections linked to your purchase orders. Track schedule, inspector and result.
        </p>

        <div className="mt-10 border border-neutral-200 bg-white rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-16 grid place-items-center">
              <Loader2 className="w-5 h-5 animate-spin text-[hsl(217,90%,25%)]" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-sm text-neutral-500">
              No inspections yet. They appear when a purchase order reaches Ready for Inspection.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[hsl(60,5%,96%)]">
                  {[
                    "Inspection ID",
                    "PO Number",
                    "Manufacturer",
                    "Factory",
                    "Country",
                    "Inspection Date",
                    "Inspector",
                    "Status",
                    "Result",
                  ].map((h) => (
                    <TableHead
                      key={h}
                      className="uppercase text-[10px] tracking-[0.2em] font-semibold text-neutral-500"
                    >
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((ins) => (
                  <TableRow
                    key={ins.id}
                    data-testid={WORKSPACE.inspectionRow}
                    className="cursor-pointer hover:bg-[hsl(60,5%,97%)]"
                    onClick={() => navigate(`/workspace/inspections/${ins.id}`)}
                  >
                    <TableCell className="font-mono text-xs text-[hsl(217,90%,25%)]">
                      {ins.number}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{ins.po_number || "—"}</TableCell>
                    <TableCell className="text-sm">{ins.manufacturer?.name || "—"}</TableCell>
                    <TableCell className="text-sm">{ins.factory || "—"}</TableCell>
                    <TableCell className="text-sm">{ins.country || "—"}</TableCell>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {ins.inspection_date || "—"}
                    </TableCell>
                    <TableCell className="text-sm">{ins.inspector || "—"}</TableCell>
                    <TableCell>
                      <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px] tracking-wider">
                        {ins.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{ins.result || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
